import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Dialog, DialogContent, DialogActions, Box, Typography } from '@mui/material';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import Barcode from 'react-barcode';
import { QRCodeSVG } from 'qrcode.react';
import { format } from 'date-fns';
import PrintIcon from '@mui/icons-material/Print';
import ReactDOM from 'react-dom/client';
import { PHYSICAL_DIRECTIONS, destinationDeliveryOf } from 'src/utils/physical-api';
import { directionOfDelivery } from './view/data';

const LABEL_WIDTH_MM = 101.6;
const LABEL_HEIGHT_MM = 152.4;

const OFFICES = {
  NY: { address: '1914 Kings Highway, Brooklyn, NY 11229', phone: '(716) 676-0022' },
  TBILISI: { address: '41 Tashkenti St, Tbilisi 0160', phone: '+995 32 247 0022' },
};

const ROUTES = {
  [PHYSICAL_DIRECTIONS.USA_GE]: { from: 'NYC', to: 'TBS', office: OFFICES.TBILISI },
  [PHYSICAL_DIRECTIONS.GE_USA]: { from: 'TBS', to: 'NYC', office: OFFICES.NY },
};

const DELIVERY_LABELS = {
  Tbilisi_Pickup: 'TBILISI PICKUP',
  Tbilisi_Delivery: 'TBILISI DELIVERY',
  Region: 'REGION',
  NY_Pickup: 'NY PICKUP',
  NY_Delivery: 'NY DELIVERY',
  US_Region: 'US REGION',
};

const personName = (person) =>
  `${person?.first_name || ''} ${person?.last_name || ''}`.trim() || '-';

const addressOf = (receiver) => {
  const address = receiver?.personalAddress?.[0] || {};
  return (
    [
      address.address1,
      address.address2,
      address.city,
      address.state,
      address.zipCode,
      address.country,
    ]
      .filter(Boolean)
      .join(', ') || '-'
  );
};

const labelData = (order) => {
  const delivery = destinationDeliveryOf(order);
  const direction =
    order?.physicalOrderGroup?.direction ??
    directionOfDelivery(delivery) ??
    PHYSICAL_DIRECTIONS.USA_GE;
  const area = order?.physicalOrderGroup?.delivery_area?.name;
  const receiver = order?.secondaryReceiver ?? order?.primaryReceiver ?? {};
  return {
    route: ROUTES[direction] ?? ROUTES[PHYSICAL_DIRECTIONS.USA_GE],
    tracking: order?.tracking_number || 'N/A',
    delivery: [DELIVERY_LABELS[delivery] ?? delivery, area].filter(Boolean).join(' · '),
    sender: {
      name: personName(order?.customer),
      phone: order?.customer?.phone,
      room: order?.customer?.room_number || order?.room_number,
    },
    receiver: {
      name: personName(receiver),
      phone: receiver.phone || '-',
      address: addressOf(receiver),
    },
    registered: order?.createdAt ? format(new Date(order.createdAt), 'MM/dd/yyyy') : '-',
    weight: order?.weight ? `${Number(order.weight)} kg` : '-',
    volume: order?.volume || '-',
  };
};

const box = { border: '1px solid #000', borderRadius: '4px', padding: '6px 8px' };
const caption = { fontSize: '9px', color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px' };

const Field = ({ label, value }) => (
  <div style={{ flex: 1, padding: '6px', textAlign: 'center' }}>
    <div style={caption}>{label}</div>
    <div style={{ fontSize: '13px', fontWeight: 'bold', marginTop: '2px' }}>{value}</div>
  </div>
);

Field.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
};

export const LabelContent = ({ orderData }) => {
  const label = labelData(orderData);
  return (
    <div
      style={{
        width: `${LABEL_WIDTH_MM}mm`,
        height: `${LABEL_HEIGHT_MM}mm`,
        padding: '10px',
        backgroundColor: 'white',
        color: 'black',
        fontFamily: 'Arial, sans-serif',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: '7px',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingBottom: '6px',
          borderBottom: '2px solid #000',
        }}
      >
        <div>
          <div style={{ fontSize: '18px', fontWeight: 900, letterSpacing: '1px' }}>GZAVNILI</div>
          <div style={{ fontSize: '22px', fontWeight: 900 }}>
            {label.route.from} → {label.route.to}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Barcode value={label.tracking} width={1.4} height={42} displayValue={false} margin={0} />
          <div style={{ fontSize: '15px', fontWeight: 'bold', letterSpacing: '1px' }}>
            {label.tracking}
          </div>
        </div>
      </div>

      {label.delivery && (
        <div
          style={{
            backgroundColor: '#000',
            color: 'white',
            textAlign: 'center',
            fontSize: '14px',
            fontWeight: 'bold',
            padding: '4px',
            borderRadius: '4px',
          }}
        >
          {label.delivery}
        </div>
      )}

      <div style={box}>
        <div style={caption}>From (sender)</div>
        <div style={{ fontSize: '13px', fontWeight: 'bold' }}>{label.sender.name}</div>
        <div style={{ fontSize: '11px' }}>
          {[label.sender.phone && `Tel ${label.sender.phone}`, label.sender.room && `Room ${label.sender.room}`]
            .filter(Boolean)
            .join(' · ')}
        </div>
      </div>

      <div style={{ ...box, display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
        <div>
          <div style={caption}>To (receiver)</div>
          <div style={{ fontSize: '15px', fontWeight: 'bold' }}>{label.receiver.name}</div>
          <div style={{ fontSize: '11px', marginTop: '2px' }}>{label.receiver.address}</div>
          <div style={{ fontSize: '12px', fontWeight: 'bold', marginTop: '2px' }}>
            Tel {label.receiver.phone}
          </div>
        </div>
        <QRCodeSVG value={label.tracking} size={72} level="M" />
      </div>

      <div style={{ ...box, display: 'flex', padding: 0 }}>
        <Field label="Registered" value={label.registered} />
        <div style={{ borderLeft: '1px solid #000' }} />
        <Field label="Weight" value={label.weight} />
        <div style={{ borderLeft: '1px solid #000' }} />
        <Field label="Volume" value={label.volume} />
      </div>

      <div style={{ fontSize: '10px', color: '#333' }}>
        <div style={caption}>Gzavnili office</div>
        <div>
          {label.route.office.address} · {label.route.office.phone}
        </div>
      </div>

      <div
        style={{
          backgroundColor: '#000',
          color: 'white',
          textAlign: 'center',
          padding: '7px',
          borderRadius: '4px',
          marginTop: 'auto',
          fontSize: '14px',
          fontWeight: 'bold',
          letterSpacing: '1px',
        }}
      >
        www.gzavnili.com
      </div>
    </div>
  );
};

LabelContent.propTypes = {
  orderData: PropTypes.object.isRequired,
};

const renderLabelImage = async (order) => {
  const container = document.createElement('div');
  Object.assign(container.style, {
    position: 'absolute',
    left: '-9999px',
    top: '0px',
    width: `${LABEL_WIDTH_MM}mm`,
    height: `${LABEL_HEIGHT_MM}mm`,
  });
  document.body.appendChild(container);
  const root = ReactDOM.createRoot(container);
  root.render(<LabelContent orderData={order} />);
  await new Promise((resolve) => {
    setTimeout(resolve, 300);
  });
  const canvas = await html2canvas(container, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    logging: false,
  });
  root.unmount();
  document.body.removeChild(container);
  return canvas.toDataURL('image/png');
};

const printLabels = async (orders) => {
  // eslint-disable-next-line new-cap
  const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: [LABEL_WIDTH_MM, LABEL_HEIGHT_MM] });
  for (let i = 0; i < orders.length; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    const image = await renderLabelImage(orders[i]);
    if (i > 0) pdf.addPage();
    pdf.addImage(image, 'PNG', 0, 0, LABEL_WIDTH_MM, LABEL_HEIGHT_MM);
  }
  const printWindow = window.open(URL.createObjectURL(pdf.output('blob')), '_blank');
  if (printWindow) {
    printWindow.onload = () => printWindow.print();
  }
};

function PrintLabel({ open, onClose, order, orders }) {
  const [printing, setPrinting] = useState(false);
  const labels = orders?.length ? orders : [order].filter(Boolean);
  if (!labels.length) return null;

  const handlePrint = async () => {
    setPrinting(true);
    try {
      await printLabels(labels);
      onClose();
    } catch (error) {
      console.error('Error generating or printing labels:', error);
    } finally {
      setPrinting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        {labels.length > 1 ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Ready to print {labels.length} labels
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Click Print to generate one PDF with all selected labels
            </Typography>
          </Box>
        ) : (
          <LabelContent orderData={labels[0]} />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint} disabled={printing}>
          {printing ? 'Preparing…' : 'Print'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

PrintLabel.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  order: PropTypes.object,
  orders: PropTypes.array,
};

PrintLabel.defaultProps = {
  order: null,
  orders: null,
};

export default PrintLabel;
